<script lang="ts">
  import { createMutation } from "@tanstack/svelte-query";
  import { t } from "../../helpers/translations";
  import { noop } from "../../helpers/utils";
  import type { EmailUser } from "../../helpers/types";
  import { updatePriceChangeEmails } from "../../api/price-history";
  import { createGetPriceChangeEmailsQuery } from "../../queries/price-history";

  import InstructionalModal from "../modals/InstructionalModal.svelte";
  import { createModal, type ModalPartAttributes } from "../modals/createModal";
  import TextNew from "../TextNew.svelte";
  import ButtonNew from "../buttons/ButtonNew.svelte";
  import ToggleButton from "../buttons/ToggleButton.svelte";
  import { selectedBuyer } from "../../stores/buyer";

  export let underlayAttrs: ModalPartAttributes;
  export let contentAttrs: ModalPartAttributes;
  export let open: boolean;
  export let onClose: () => void = noop;
  export let contentAction: ReturnType<typeof createModal>["content"];

  let users: Array<EmailUser> = [];

  function onToggle(recipient: EmailUser) {
    let updatedUsers = users;
    updatedUsers = updatedUsers.map((r) => {
      if (r.email === recipient.email) {
        r.subscribed = !r.subscribed;
      }
      return r;
    });
    users = updatedUsers;
  }

  $: buyerId = $selectedBuyer?.id ?? "";
  $: priceChangeEmailsQuery = createGetPriceChangeEmailsQuery(buyerId);
  $: users = $priceChangeEmailsQuery.data?.users ?? [];

  const updateMutation = createMutation({
    mutationFn: (users: Array<EmailUser>) => {
      return updatePriceChangeEmails(buyerId, users);
    },
    onError: (error) => {
      console.error("Failed to update price change emails", error);
    },
    onSuccess: () => {
      onClose();
    },
  });
</script>

<InstructionalModal
  {open}
  {underlayAttrs}
  {contentAttrs}
  {contentAction}
  backgroundImage="/static/assets/images/price-changes-email.jpg"
>
  <div slot="leftPanel" class="modal-left">
    <div class="modal-left-content">
      <div class="title">
        <TextNew weight="medium" size="xl" --rk-text-color="var(--rk-text-light)">
          {t("priceChanges.modal.title")}
        </TextNew>
      </div>
    </div>
  </div>

  <div slot="rightPanel" class="modal-right">
    <div class="list" style:overflow={$priceChangeEmailsQuery.data ? "auto" : undefined}>
      {#if $priceChangeEmailsQuery.data}
        {#each users as recipient}
          <div class="row-container">
            <button class="recipient-row" on:click={() => onToggle(recipient)}>
              <div class="recipient-info">
                <TextNew size="s">{recipient.name}</TextNew>
                <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)">
                  {recipient.email}
                </TextNew>
              </div>
              <ToggleButton size={56} active={recipient.subscribed} onToggle={noop} />
            </button>
          </div>
        {/each}
      {/if}
    </div>
    <ButtonNew
      isLoading={$updateMutation.isPending}
      loadingSize={36}
      variant="primary"
      size="l"
      fullWidth
      on:click={() => $updateMutation.mutate(users)}
    >
      <TextNew size="l" weight="regular">
        {t("global.save.title")}
      </TextNew>
    </ButtonNew>
  </div>
</InstructionalModal>

<style>
  .modal-left {
    position: relative;
    flex: 0 1 416px;
    padding: 16px;
  }

  .modal-left-content {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .title {
    padding: 16px;
  }

  .modal-right {
    flex: 0 1 544px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 16px 16px 0px;
    height: 100%;
  }

  .list {
    border-radius: 8px;
    background-color: white;
  }

  .row-container {
    display: flex;
    flex-direction: column;
    padding: 0px 24px;
  }

  .recipient-row {
    display: flex;
    width: 100%;
    padding: 20px 0;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--rk-divider-0);
  }

  .recipient-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
</style>
